import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { countCreatives, CreativeStatus } from '@/services/ws-creatives/src/main/resources/rest-api';
import { getPersonalComments } from '@/services/ws-orders/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Media } from '@/js/libs/ui';
import { useIdentityRoles } from '@/js/libs/use-auth';
import { NotActive, useMappedRequest, useRequest } from '@/js/libs/use-request';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage';
import { ErrorCode } from 'src/types';

export const { useNavigationContext } = createContexts({
  Navigation() {
    const { saveBoolean, loadBoolean } = useLocalStorage();
    const isMobile = useMediaQuery({ query: Media.xsMax });
    const { ADMIN, CUSTOMER, MANAGE_DIGITAL_PRODUCTS, OPS_PUSH } = useIdentityRoles();
    const [isCollapsedNav, setCollapsedNav] = useState(isMobile ? true : loadBoolean(StorageKey.PUZZLE_NAV_COLLAPSED));

    const getCommentsRequest = useRequest({
      apiMethod: getPersonalComments,
      params: ADMIN ? { showRead: false, page: 0, limit: 10 } : NotActive,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const handleSetUnreadCommentsCount = useCallbackRef((isDecreasing?: boolean) => {
      getCommentsRequest.setData(({ responseCode, data }) => ({
        responseCode,
        data: { ...data, total: isDecreasing ? data.total - 1 : data.total + 1 },
        headers: null,
      }));
    });

    const unreadCommentsCountRequest = useMappedRequest(getCommentsRequest, (data) => data.total ?? 0);
    const pendingUnreadCommentsCount = useSubscribe(getCommentsRequest.pending$);

    const countCreativesRequest = useRequest({
      apiMethod: countCreatives,
      params:
        !ADMIN && !(MANAGE_DIGITAL_PRODUCTS && OPS_PUSH && !CUSTOMER) ? NotActive : { creativeStatus: CreativeStatus.PENDING_APPROVAL },
    });

    const articlesCount = useSubscribe(countCreativesRequest.data$, (response) => response?.data ?? 0);
    const pendingArticlesCount = useSubscribe(countCreativesRequest.pending$);

    const collapseMainNav = useCallbackRef(() => {
      saveBoolean(StorageKey.PUZZLE_NAV_COLLAPSED, !isCollapsedNav);
      setCollapsedNav(!isCollapsedNav);
    });

    return {
      isCollapsedNav,
      collapseMainNav,
      unreadCommentsCountRequest,
      pendingUnreadCommentsCount,
      handleSetUnreadCommentsCount,
      reloadUnreadComments: getCommentsRequest.retry,
      articlesCount,
      pendingArticlesCount,
      reloadApprovalCounter: countCreativesRequest.retry,
    };
  },
});
